/**
 * Constantes y catálogos compartidos del Reporte Diario embebido en la SO.
 * Vive aparte para evitar dependencias circulares entre AvancesTab y
 * ReporteDiarioTabs.
 *
 * Los catálogos de equipos y personal provienen del RD viajero (versión de
 * diseño en HTML): las tablas de Vehículos/Maquinaria y Personal muestran
 * SIEMPRE este listado fijo; el contratista sólo digita las cantidades.
 */

import type { Jornada, Recursos, Rd } from "@app-types/entities";
import type { OrigenPersonal, ClasePersonal } from "@constants/catalogos";

/** Horas entre dos horas "HH:MM" (nunca negativo). */
export function horasEntre(ini: string, fin: string): number {
  if (!ini || !fin) return 0;
  const [hi = 0, mi = 0] = ini.split(":").map(Number);
  const [hf = 0, mf = 0] = fin.split(":").map(Number);
  return Math.max(0, (hf * 60 + mf - (hi * 60 + mi)) / 60);
}

/** Cálculo de la jornada: total (− almuerzo), horas de lluvia y efectiva. */
export function calcJornada(j: Jornada): {
  totalHrs: number;
  horasLluvia: number;
  efectiva: number;
} {
  const totalHrs = Math.max(
    0,
    horasEntre(j.horaInicio, j.horaFin) - (j.descansosMinutos || 0) / 60,
  );
  const horasLluvia = (j.tormentas ?? []).reduce(
    (s, t) => s + horasEntre(t.horaIni, t.horaFin),
    0,
  );
  return { totalHrs, horasLluvia, efectiva: Math.max(0, totalHrs - horasLluvia) };
}

/**
 * Fecha de HOY en la zona horaria LOCAL del PC del usuario (YYYY-MM-DD).
 * Se calcula EN CADA LLAMADA — no queda congelada al cargar la app (una sesión
 * que cruza la medianoche se actualiza en el siguiente render) y no usa UTC
 * (antes, desde las ~19:00 hora de Colombia "hoy" ya era mañana).
 * Regla de negocio: solo el día actual es editable; lo no reportado ayer se
 * acumula en el reporte de hoy.
 */
export function hoyLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Fecha local (YYYY-MM-DD) de un timestamp ISO — para comparar contra hoyLocal(). */
export function fechaLocalDe(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const JORNADA_DEFAULT: Jornada = {
  horaInicio: "07:00",
  horaFin: "17:00",
  descansosMinutos: 60,
  horasLluvia: 0,
  horasTormenta: 0,
  condicionClima: "Soleado",
  jornadaEfectivaHoras: 9,
  vientos: "",
  tormentas: [],
  actividadesHoy: "",
  actividadesManana: "",
};

// ── Catálogos del RD viajero ────────────────────────────────────────────────

/** Maquinaria amarilla (consumo gal/h). */
export const CAT_MAQ_AMARILLA: string[] = [
  "Bulldozer sobre orugas",
  "MotoNiveladora - vías/plataformas",
  "Retro cargador (Pajarita)",
  "Retro Excavadora sobre oruga",
  "VibroCompactador",
  "Minicargador",
];

/** Máquinas / herramientas mayores (consumo gal/h). */
export const CAT_MAQ_HERRAMIENTAS: string[] = [
  "Motosoldadores",
  "Compresor aire",
  "MIXER - AutoHormigonera",
  'Grúa de +80 tons',
  "Grúa de hasta 80 tons",
  "Manlift",
  "Torre de iluminación",
  'Dobladora Tubos hasta 6"',
  'Dobladora Tubos mayor a 6"',
  "Motobomba",
  "Carro canasta - Eléctrico",
  "Camión Grúa 10 a 15 tons",
];

/** Vehículos de movilización de personal (consumo km/gal). */
export const CAT_VEH_MOVILIZACION: string[] = [
  "Buseta 20 pasajeros",
  "Buseta 40 pasajeros",
  "Camioneta 4x4",
  "Turbo Camión",
];

/** Vehículos de carga (consumo km/gal). */
export const CAT_VEH_CARGA: string[] = [
  "Cama alta - cabezote 450-560HP",
  "Cama baja - cabezote 450-560HP",
  "CarroTanque sencillo 2200 gal",
  "CarroTanque doble T 4000 gal",
  "CarroTanque mula 10.000 gal",
  "Volqueta",
];

/** Herramientas menores. */
export const CAT_HERRAMIENTAS: string[] = [
  "Andamios",
  "Autocontenido",
  "Cajas Soldadores",
  "Cajas tuberos",
  "Contenedores",
  "Dron",
  "Equipo de Sandblasting",
  "Equipo de Topografía",
  "Inversores",
  "Monitor de atmósferas",
];

/** Roles de personal en obra. */
export const CAT_PERSONAL: string[] = [
  "ADMINISTRADOR",
  "ALMACENISTA",
  "APAREJADOR",
  "AUXILIAR/VIGÍA HSE",
  "AYUDANTE MECÁNICO",
  "AYUDANTE TÉCNICO",
  "CADENERO",
  "CONDUCTORES",
  "COORDINADOR HSE",
  "DIRECTOR DE OBRA",
  "DOBLADOR",
  "INGENIERO RESIDENTE",
  "MECÁNICO MTTO",
  "OBREROS",
  "OFICIAL CIVIL",
  "OPERADOR DE CAMIÓN GRÚA",
  "OPERADORES DE MAQUINARIA",
  "PINTOR / SADBLASTERO",
  "PROFESIONAL AMBIENTAL",
  "PROGRAMADOR DE OBRA",
  "SEÑALIZADORES",
  "SOLDADOR 1A",
  "SOLDADOR PAILERO",
  "SUPERVISOR DE OBRA",
  "SUPERVISOR HSE",
  "SUPERVISOR QA/QC",
  "TÉCNICO ELECTRICISTA",
  "TOPÓGRAFO",
  "TUBERO/ALINEADOR",
  "VIGILANTE",
];

/** Clasificación por defecto del personal (STAFF / MOC / MONC). */
const DEFAULT_CLASIF: Record<string, ClasePersonal> = {
  ADMINISTRADOR: "STAFF",
  ALMACENISTA: "STAFF",
  "COORDINADOR HSE": "STAFF",
  "DIRECTOR DE OBRA": "STAFF",
  "INGENIERO RESIDENTE": "STAFF",
  "PROFESIONAL AMBIENTAL": "STAFF",
  "PROGRAMADOR DE OBRA": "STAFF",
  "SUPERVISOR DE OBRA": "STAFF",
  "SUPERVISOR HSE": "STAFF",
  "SUPERVISOR QA/QC": "STAFF",
  TOPÓGRAFO: "STAFF",
  APAREJADOR: "MOC",
  CADENERO: "MOC",
  CONDUCTORES: "MOC",
  DOBLADOR: "MOC",
  "MECÁNICO MTTO": "MOC",
  "OFICIAL CIVIL": "MOC",
  "OPERADOR DE CAMIÓN GRÚA": "MOC",
  "OPERADORES DE MAQUINARIA": "MOC",
  "PINTOR / SADBLASTERO": "MOC",
  "SOLDADOR 1A": "MOC",
  "SOLDADOR PAILERO": "MOC",
  "TÉCNICO ELECTRICISTA": "MOC",
  "TUBERO/ALINEADOR": "MOC",
  "AUXILIAR/VIGÍA HSE": "MONC",
  "AYUDANTE MECÁNICO": "MONC",
  "AYUDANTE TÉCNICO": "MONC",
  OBREROS: "MONC",
  SEÑALIZADORES: "MONC",
  VIGILANTE: "MONC",
};

/**
 * Clase de un rol. Se exporta porque el Registro de recursos necesita saber
 * cual es STAFF: es la unica clase que se reparte sola entre las OT.
 */
export const claseDe = (rol: string): ClasePersonal => DEFAULT_CLASIF[rol] ?? "MOC";
/** STAFF tiende a foráneo; MOC/MONC tiende a local (VD §6.7). */
const origenDe = (rol: string): OrigenPersonal =>
  claseDe(rol) === "STAFF" ? "Foraneo" : "Local";

/**
 * Recursos por defecto: el catálogo completo con cantidades en 0. El RD del
 * día arranca con todas las filas; el contratista digita las cantidades.
 */
export const RECURSOS_DEFAULT: Recursos = {
  maqAmarilla: CAT_MAQ_AMARILLA.map((equipo) => ({ equipo, cantidad: 0, horas: 0, galones: 0 })),
  maqHerramientas: CAT_MAQ_HERRAMIENTAS.map((equipo) => ({ equipo, cantidad: 0, horas: 0, galones: 0 })),
  vehMovilizacion: CAT_VEH_MOVILIZACION.map((equipo) => ({ equipo, cantidad: 0, km: 0, galones: 0 })),
  vehCarga: CAT_VEH_CARGA.map((equipo) => ({ equipo, cantidad: 0, km: 0, galones: 0 })),
  personal: CAT_PERSONAL.map((rol) => ({
    rol,
    origen: origenDe(rol),
    clase: claseDe(rol),
    cantidad: 0,
  })),
  herramientas: CAT_HERRAMIENTAS.map((herramienta) => ({ herramienta, cantidad: 0 })),
};

/**
 * Valores con que se crea el RD del día cuando aún no existe. El catálogo de
 * recursos se incluye completo para que las pestañas siempre lo muestren.
 */
export function rdCreateDefaults(): Pick<
  Rd,
  "jornada" | "recursos" | "avancesTareas" | "eventos"
> {
  return {
    jornada: JORNADA_DEFAULT,
    recursos: RECURSOS_DEFAULT,
    avancesTareas: [],
    eventos: [],
  };
}

/**
 * Pre-carga recursos del día tomando las CANTIDADES del RD anterior y
 * reseteando los campos de medida diaria (horas, km, galones). Sirve para
 * que el contratista no tenga que volver a digitar las mismas cantidades de
 * personal / vehículos / maquinaria todos los días — solo ajusta novedades.
 *
 * - `cantidad` se conserva del día anterior.
 * - `horas`, `km`, `galones` se ponen en 0 (son medidas del día actual).
 * - Si no hay día anterior o el RD viene vacío, devuelve `RECURSOS_DEFAULT`.
 */
export function recursosPrecargadosDeAyer(ayer?: Recursos): Recursos {
  if (!ayer) return RECURSOS_DEFAULT;
  // Pasamos por el helper de catálogo para garantizar el orden y completitud
  // — luego reseteamos los campos diarios.
  const base = recursosConCatalogo(ayer);
  return {
    maqAmarilla: base.maqAmarilla.map((i) => ({ ...i, horas: 0, galones: 0 })),
    maqHerramientas: base.maqHerramientas.map((i) => ({ ...i, horas: 0, galones: 0 })),
    vehMovilizacion: base.vehMovilizacion.map((i) => ({ ...i, km: 0, galones: 0 })),
    vehCarga: base.vehCarga.map((i) => ({ ...i, km: 0, galones: 0 })),
    personal: base.personal, // personal solo tiene cantidad; nada que resetear
    herramientas: base.herramientas, // ídem
  };
}

/**
 * Combina los recursos guardados de un RD con el catálogo: devuelve SIEMPRE
 * el catálogo completo y en orden, conservando las cantidades ya registradas
 * (match por nombre de equipo / rol / herramienta).
 */
export function recursosConCatalogo(guardados?: Recursos): Recursos {
  const idx = <T,>(arr: T[] | undefined, key: keyof T): Map<unknown, T> => {
    const m = new Map<unknown, T>();
    for (const x of arr ?? []) m.set(x[key], x);
    return m;
  };
  const mA = idx(guardados?.maqAmarilla, "equipo");
  const mH = idx(guardados?.maqHerramientas, "equipo");
  const vM = idx(guardados?.vehMovilizacion, "equipo");
  const vC = idx(guardados?.vehCarga, "equipo");
  const pe = idx(guardados?.personal, "rol");
  const he = idx(guardados?.herramientas, "herramienta");
  return {
    maqAmarilla: RECURSOS_DEFAULT.maqAmarilla.map((b) => ({ ...b, ...(mA.get(b.equipo) ?? {}) })),
    maqHerramientas: RECURSOS_DEFAULT.maqHerramientas.map((b) => ({ ...b, ...(mH.get(b.equipo) ?? {}) })),
    vehMovilizacion: RECURSOS_DEFAULT.vehMovilizacion.map((b) => ({ ...b, ...(vM.get(b.equipo) ?? {}) })),
    vehCarga: RECURSOS_DEFAULT.vehCarga.map((b) => ({ ...b, ...(vC.get(b.equipo) ?? {}) })),
    personal: RECURSOS_DEFAULT.personal.map((b) => ({ ...b, ...(pe.get(b.rol) ?? {}) })),
    herramientas: RECURSOS_DEFAULT.herramientas.map((b) => ({ ...b, ...(he.get(b.herramienta) ?? {}) })),
  };
}
